/**
 * Live currency conversion using open.er-api.com (free, no key, 170+ currencies).
 * Rates are fetched per-base and cached for the app session.
 *
 * Business currency is the settlement currency for all orders.
 * Zone prices in foreign currencies are converted to it automatically.
 */

// Cache: base currency → { target: rate }
const rateCache: Record<string, Record<string, number>> = {};

/**
 * Fetch all rates for a given base currency and cache them.
 * Uses primary API (open.er-api.com) with fallback to fixer.io format if available.
 * Returns null on failure.
 */
async function fetchRatesForBase(base: string): Promise<Record<string, number> | null> {
  if (rateCache[base]) {
    console.log(`[CurrencyConverter] Using cached rates for ${base}`);
    return rateCache[base];
  }
  try {
    console.log(`[CurrencyConverter] Fetching rates for ${base} from API...`);
    
    // Try primary API: open.er-api.com (free, no key, 170+ currencies)
    const res = await fetch(
      `https://open.er-api.com/v6/latest/${base}`,
      { signal: AbortSignal.timeout(6000) }
    );
    
    if (!res.ok) {
      console.warn(`[CurrencyConverter] Primary API returned non-OK status: ${res.status} ${res.statusText}`);
      return null;
    }
    
    const data = await res.json();
    if (data?.result !== 'success' || !data?.rates) {
      console.warn(`[CurrencyConverter] Invalid API response:`, { result: data?.result, hasRates: !!data?.rates });
      return null;
    }
    
    console.log(`[CurrencyConverter] Cached rates for ${base}:`, Object.keys(data.rates).slice(0, 5), '...');
    rateCache[base] = data.rates;
    return data.rates;
  } catch (error) {
    console.error(`[CurrencyConverter] Failed to fetch rates for ${base}:`, error);
    
    // Try fallback API: exchangerate-api.com (may have rate limits)
    try {
      console.log(`[CurrencyConverter] Trying fallback API for ${base}...`);
      const fallbackRes = await fetch(
        `https://api.exchangerate-api.com/v4/latest/${base}`,
        { signal: AbortSignal.timeout(6000) }
      );
      
      if (!fallbackRes.ok) {
        console.warn(`[CurrencyConverter] Fallback API returned non-OK status: ${fallbackRes.status}`);
        return null;
      }
      
      const fallbackData = await fallbackRes.json();
      if (!fallbackData?.rates) {
        console.warn(`[CurrencyConverter] Fallback API invalid response`);
        return null;
      }
      
      console.log(`[CurrencyConverter] Fallback API success for ${base}`);
      rateCache[base] = fallbackData.rates;
      return fallbackData.rates;
    } catch (fallbackError) {
      console.error(`[CurrencyConverter] Fallback API also failed:`, fallbackError);
      return null;
    }
  }
}

/**
 * Get the exchange rate from `from` to `to`.
 * Returns 1 if currencies are the same or if the fetch fails (non-fatal).
 */
export async function getExchangeRate(from: string, to: string): Promise<number> {
  if (!from || !to || from === to) {
    console.log(`[CurrencyConverter] Same currency: ${from} === ${to}, rate = 1`);
    return 1;
  }
  const rates = await fetchRatesForBase(from);
  if (!rates) {
    console.warn(`[CurrencyConverter] Failed to get rates for ${from}→${to}, defaulting to 1`);
    return 1;
  }
  const rate = rates[to] ?? 1;
  console.log(`[CurrencyConverter] Exchange rate ${from}→${to}: ${rate}`);
  return rate;
}

/**
 * Convert `amount` from `from` currency to `to` currency.
 * Returns the original amount unchanged if currencies match or fetch fails.
 */
export async function convertAmount(amount: number, from: string, to: string): Promise<number> {
  if (!from || !to || from === to || !amount) {
    console.log(`[CurrencyConverter] Convert amount skipped (same/empty): ${amount} ${from}→${to}`);
    return amount;
  }
  const rate = await getExchangeRate(from, to);
  const result = amount * rate;
  console.log(`[CurrencyConverter] Converted: ${amount} ${from} × ${rate} = ${result} ${to}`);
  return result;
}

/** Clear the cache (e.g. on business switch). */
export function clearRateCache(): void {
  Object.keys(rateCache).forEach(k => delete rateCache[k]);
}
